import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import "./form.css";

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
  BarChart, Bar, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';

import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';

import { domain, domainUrl, formBase } from "../../data/dummyData";
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import ChartSection from "../../components/ChartSection";

function DataFormAnswer() {

  const { formId } = useParams();
  const [data, setData] = useState([]);
  const [form, setForm] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [loadingf, setLoadingf] = useState(true);
  const [keyRequired, setKeyRequired] = useState(false);
  const [accessKey, setAccessKey] = useState("");
  const [error, setError] = useState("");

  const [location, setLocation] = useState(null);


  const [modalContent, setModalContent] = useState(null);
  const [modalType, setModalType] = useState(null); // 'image', 'video', 'audio', 'document', 'map'


  const fetchForm = async () => {
    try {
      const { data } = await axios.post(`${domainUrl}/emy_answer_data`, { 
        id:formId,
        lat: location?.lat,
        long: location?.long,
      });

      if (data.success) {
        // console.log('data', data)
        setResponses(data.responses || []);
        setForm(data.form || {});
        setData(data); // data
      } else {
        if (data.reason == 'key_required') {
          setKeyRequired(true);
        } else {
          setError(data.message);
        }
      }
    } catch (err) {
      setError("Failed to load form.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitKey = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${domainUrl}/emy_answer_data`, { 
        id:formId, 
        key: accessKey,
        lat: location?.lat,
        long: location?.long,  
      });

      if (data.success) {
        setResponses(data.responses || []);
        setForm(data.form || {});
        setData(data); // data
        setKeyRequired(false);
      } else {
        setError(data.message);
      }
      setLoading(false);
    } catch (err) {
      setError("Invalid key or error occurred.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
    (pos) => {
        setLocation({
        lat: pos.coords.latitude,
        long: pos.coords.longitude,
        });
    },
    (err) => console.warn('Geolocation error:', err)
    );

    fetchForm();
  }, [formId]);

  const [parsedResponses, setParsedResponses] = useState([]);
  const [fields, setFields] = useState([]);
  const [dynamicColumns, setDynamicColumns] = useState([]);
  const [classifications, setClassifications] = useState({});

  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // or 'desc'

  // charts
  const [chartData, setChartData] = useState(null);
  const [chartColumns, setChartColumns] = useState([]);
  const [columnColors, setColumnColors] = useState({});
  const [pieColumn, setPieColumn] = useState('');
  const [chartType, setChartType] = useState('line'); // 'line', 'bar', 'pie'
  const [labelChart, setLabelChart] = useState('');

  const [mainData, setMainData] = useState([]);

  useEffect(() => {
    if (responses && Array.isArray(responses)) {
      const parsed = responses.map(entry => {
        try {
          return {
            ...entry,
            parsed: JSON.parse(entry.data)
          };
        } catch (error) {
          console.error('Invalid JSON', error);
          return {
            ...entry,
            parsed: {}
          };
        }
      });
      setParsedResponses(parsed);

      if (form?.is_file == 1) {
        setFields(form?.data ? parsed[0]?.parsed?.fields || [] : []);

        const mdata = parsed.map(da => {
          try {
            const fieldsString = da?.parsed?.fields;
        
            if (!fieldsString) {
              console.warn("Missing 'parsed.fields' in:", da);
              return {};
            }
        
            const fields = fieldsString;
        
            const mapped = fields.reduce((acc, field) => {
              const value = da.parsed[field.name];
              acc[field.label] = value ?? null;
              return acc;
            }, {});
            
            return mapped;
        
          } catch (err) {
            console.error('Error parsing fields:', err, da);
            return {};
          }
        });
        setMainData(mdata)

      } else {
        setFields((form?.data && responses.length>0) ? JSON.parse(parsed[0]?.parsed?.fields) || [] : []);
        // setFields(form?.data ? JSON.parse(parsed[0]?.parsed?.fields) || [] : []);

        const mdata = parsed.map(da => {
          try {
            const fieldsString = da?.parsed?.fields;
        
            if (!fieldsString) {
              console.warn("Missing 'parsed.fields' in:", da);
              return {};
            }
        
            const fields = JSON.parse(fieldsString);
        
            const mapped = fields.reduce((acc, field) => {
              const value = da.parsed[field.name];
              acc[field.label] = value ?? null;
              return acc;
            }, {});
            
            return mapped;
        
          } catch (err) {
            console.error('Error parsing fields:', err, da);
            return {};
          }
        });
        setMainData(mdata)
      }

    }
  }, [responses]);


  // First, collect all possible classification keys
  const allClassKeys = new Set();
  Object.values(classifications).forEach(obj => {
    Object.keys(obj).forEach(key => allClassKeys.add(key));
  });

  const merged = mainData.map((row, index) => {
    const classification = classifications[(index + 1).toString()] || {};

    // Ensure all keys are present
    const completeClassification = {};
    allClassKeys.forEach(key => {
      completeClassification[key] = classification[key] || "";
    });

    return {
      ...row,
      ...completeClassification
    };
  });
  // console.log('merged', merged)

  const handleChartLabel = (selectedLabel) => {
    setLabelChart(selectedLabel);
  };


  const exportMergedToExcel = () => {
    if (!merged || !merged.length) return;
  
    // Convert array of objects to worksheet
    const worksheet = XLSX.utils.json_to_sheet(merged);
  
    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `EmyLab Form Modified`);
  
    // Write file and trigger download
    const sheetName = `EmyLab-${form?.name || 'Form'}-Modified-Responses`;

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

    saveAs(blob, `${sheetName}-${Date.now()}.xlsx`);
    // saveAs(blob, `EmyLab_${form.name}_modified_data_${Date.now()}.xlsx`);
  };

  const exportToExcel = () => {
    const headers = [
      '#',
      ...fields.map(f => f.label),
      'Location',
      'Submitted At',
      ...dynamicColumns
    ];
  
    const data = displayedResponses.map((entry, i) => {
      const row = [i + 1];
  
      fields.forEach(field => {
        const value = entry.parsed[field.name];
        const { type, content } = detectType(value);
  
        if (type === 'map') {
          row.push('View Map');
        } else if (type === 'image' || type === 'video' || type === 'audio' || type === 'document') {
          row.push(`View ${type}`);
        } else if (type === 'link') {
          row.push(content);
        } else {
          try {
            let realContent = content;
            if (typeof content === 'string' && (content.startsWith('{') || content.startsWith('['))) {
              realContent = JSON.parse(content);
            }
  
            if (Array.isArray(realContent)) {
              row.push(realContent.length ? realContent.join(', ') : 'NA');
            } else if (typeof realContent === 'object' && realContent !== null) {
              row.push(Object.keys(realContent).length ? JSON.stringify(realContent) : 'NA');
            } else if (realContent === null || realContent === undefined || realContent === '') {
              row.push('NA');
            } else {
              row.push(typeof realContent === 'string' ? realContent.replace(/^"|"$/g, '') : realContent);
            }
          } catch (e) {
            row.push(content || 'NA');
          }
        }
      });
  
      // Location
      row.push(entry.lat && entry.long ? `(${entry.lat}, ${entry.long})` : 'NA');
  
      // Created at
      row.push(new Date(entry.created_at).toLocaleString());
  
      // Dynamic columns
      dynamicColumns.forEach(col => {
        row.push(classifications[entry.id]?.[col] || '');
      });
  
      return row;
    });
  
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const workbook = XLSX.utils.book_new();

    const sheetName = `EmyLab-${form?.name || 'Form'}-Responses`;
    // XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.utils.book_append_sheet(workbook, worksheet, `EmyLab Form Responses`);
  
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });

    saveAs(blob, `${sheetName}-${Date.now()}.xlsx`);
    // saveAs(blob, `EmyLab_${form.name}_responses_${Date.now()}.xlsx`);
  };

  const openModal = (content, type) => {
    setModalContent(content);
    setModalType(type);
  };

  const closeModal = () => {
    setModalContent(null);
    setModalType(null);
  };

  const detectType = (value) => {
    if (!value) return { type: 'text', content: '' };

    if (typeof value === 'object' && value.lat !== undefined && value.long !== undefined) {
      return { type: 'map', content: value };
    }

    if (typeof value === 'string') {
      const ext = value.split('.').pop().toLowerCase();
      
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return { type: 'image', content: value };
      if (['mp4', 'mov', 'avi'].includes(ext)) return { type: 'video', content: value };
      if (['mp3', 'wav', 'm4a'].includes(ext)) return { type: 'audio', content: value };
      if (['pdf', 'doc', 'docx', 'xls', 'xlsx'].includes(ext)) return { type: 'document', content: value };
      
      if (value.startsWith('http')) return { type: 'link', content: value };
    }

    return { type: 'text', content: JSON.stringify(value) };
  };


  // classify

  const addColumn = () => {
    const columnName = prompt("Enter new column name:");
    if (columnName) {
      setDynamicColumns([...dynamicColumns, columnName]);
    }
  };

  const handleClassificationChange = (rowId, columnName, value) => {
    setClassifications(prev => ({
      ...prev,
      [rowId]: {
        ...(prev[rowId] || {}),
        [columnName]: value,
      }
    }));
  };

  const removeColumn = (colToRemove) => {
    setDynamicColumns(prev => prev.filter(col => col !== colToRemove));
    
    // Optionally also clean classifications related to that column
    setClassifications(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(entryId => {
        if (updated[entryId][colToRemove] !== undefined) {
          delete updated[entryId][colToRemove];
        }
      });
      return updated;
    });
  };

  const sortedResponses = [...parsedResponses].sort((a, b) => {
    let aValue, bValue;
  
    // Check if it's a dynamic column
    if (dynamicColumns.includes(sortField)) {
      aValue = classifications[a.id]?.[sortField] || '';
      bValue = classifications[b.id]?.[sortField] || '';
    }
    // Or if it's a regular field
    else if (fields.some(f => f.label === sortField)) {
      const fieldName = fields.find(f => f.label === sortField)?.name;
      aValue = a.parsed[fieldName] || '';
      bValue = b.parsed[fieldName] || '';
    }
    // Or if it's a special field like submission time
    else if (sortField === 'created_at') {
      aValue = a.created_at;
      bValue = b.created_at;
    }
  
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
  
    return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const handleSortFieldChange = (selectedLabel) => {
    if (selectedLabel === sortField) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(selectedLabel);
      setSortOrder('asc');
    }
  };
  const displayedResponses = sortedResponses.length > 0 ? sortedResponses : parsedResponses;


  if (loading) return (
    <>
      <Header page={'form'} subpage={true}/>

      <div className="container-fluid contact py-5">
        <div className="container py-5">
            <div className="row g-5">

                <div className="col-lg-6 wow mx-auto fadeInRight" data-wow-delay="0.4s">
                    <div className="form-section bg-success p-5 h-100">
                      <div className="text-center">
                        <h1 className="display-4 text-white mb-4">Loading Form...</h1>
                      </div>
                    </div>
                </div>
            </div>
        </div>
      </div>

      <Footer subpage={true}/>
    </>
  );

  if (error) return (
    
    <>
        <Header page={'form'} subpage={true}/>

        <div className="container-fluid contact py-5">
          <div className="container py-5">
              <div className="row g-5">

                  <div className="col-lg-6 wow mx-auto fadeInRight" data-wow-delay="0.4s">
                      <div className="form-section bg-success p-5 h-100">
                        <div className="text-center">
                          <h1 className="display-4 text-white mb-4">{error}</h1>
                        </div>
                      </div>
                  </div>
              </div>
          </div>
        </div>

        <Footer subpage={true}/>
      </>
  );

  if (keyRequired) {
    return (

      <>
        <Header page={'form'} subpage={true}/>

          <div className="container-fluid contact py-5">
            <div className="container py-5">
                <div className="row g-5">

                    <div className="col-lg-6 wow mx-auto fadeInRight" data-wow-delay="0.4s">
                        <div className="form-section bg-success p-5 h-100">


                          <div className="text-center">
                            <h1 className="display-4 text-white mb-4">🔒 Private Form</h1>
                            <p className="text-white">Please enter the Access Key to view this form:</p>

                            <div className="col-6 mx-auto">
                              <div className="form-floating form-section-col">
                                <input 
                                  type="text" 
                                  value={accessKey} 
                                  onChange={(e) => setAccessKey(e.target.value)} 
                                  // className="form-control" 
                                  className="form-control col-4"
                                />
                              </div>
                            </div>

                            
                            <button onClick={handleSubmitKey} className="btn btn-primary mt-3">Submit Key</button>
                          </div>
                            
                          
                        </div>
                    </div>
                </div>
            </div>
          </div>

        <Footer subpage={true}/>
      </>
    );
  }


  return (
    
    <>
      <Header page={'form'} subpage={true}/>

      <div className="container-fluid contact py-5">
        <div className="container py-5">
            <div className="row g-5">

                <div className="col-lg-12 wow mx-auto fadeInRight" data-wow-delay="0.4s">
                    <div className="form-section bg-success p-5 h-100">
                        
                      <h1 className="display-4 text-white mb-4">{form.name} 📄 Responses</h1>

                      {displayedResponses.length > 0? <>

                      <div>
                        <div className="row">
                          <button className="btn btn-outline-primary mb-3 col-3 mx-1" onClick={exportToExcel}>
                            ⬇️ Export as CSV
                          </button>
                          <button onClick={addColumn} className="btn btn-outline-primary mb-3 col-3">
                            + Add Column
                          </button>

                          <button onClick={exportMergedToExcel} className="btn btn-outline-primary mb-3 col-3 mx-1">
                            Export Modified
                          </button>

                          <button
                            className="btn btn-outline-primary mb-3 col-2 mx-1"
                            onClick={() => setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))}
                            disabled={!sortField}
                          >
                            {sortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
                          </button>

                          
                          
                        </div>

                        <div className="row mx-auto">
                          <div className="my-2 text-white col-4">
                            <label className="fw-bold me-2">Sort By:</label>
                            <select className="form-select w-auto d-inline" onChange={(e) => handleSortFieldChange(e.target.value)}>
                              <option value="">-- Sort By --</option>
                              {fields.map((field, idx) => (
                                <option key={idx} value={field.label}>{field.label}</option>
                              ))}
                              {dynamicColumns.map((col, idx) => (
                                <option key={`dyn-sort-${idx}`} value={col}>{col}</option>
                              ))}
                              <option value="created_at">Submission Time</option>
                            </select>
                          </div>

                          <div className="my-2 text-white col-4">
                            <label className="fw-bold me-2">Chart Type:</label>
                            <select
                              className="form-select w-auto d-inline"
                              value={chartType}
                              onChange={(e) => {
                                const type = e.target.value;
                                setChartType(type);
                                if (type === 'pie' && chartColumns.length > 0) {
                                  setPieColumn(chartColumns[0]); // pie only allows 1 column
                                }
                              }}
                            >
                              <option value="line">Line</option>
                              <option value="bar">Bar</option>
                              <option value="pie">Pie</option>

                              <option value="doughnut">Doughnut</option>
                              <option value="radar">Radar</option>
                              <option value="polararea">Polararea</option>
                            </select>
                          </div>

                          <div className="my-2 text-white col-4">
                            <label className="fw-bold me-2">Chart label:</label>
                            <select className="form-select w-auto d-inline" onChange={(e) => handleChartLabel(e.target.value)}>
                              <option value="">-- label By --</option>
                              {fields.map((field, idx) => (
                                <option key={idx} value={field.label}>{field.label}</option>
                              ))}
                              {dynamicColumns.map((col, idx) => (
                                <option key={`dyn-sort-${idx}`} value={col}>{col}</option>
                              ))}
                              <option value="created_at">Submission Time</option>
                            </select>
                          </div>
                        </div>
                        

                        <div className="my-3 my-2 text-white">
                          <label className="fw-bold">Chart Columns:</label>
                          <div className="d-flex flex-wrap gap-3">
                            {[...fields.map(f => f.label), ...dynamicColumns].map((col, idx) => (
                              <div key={idx} className="d-flex align-items-center">
                                <input
                                  type="checkbox"
                                  checked={chartColumns.includes(col)}
                                  onChange={(e) => {
                                    const newSelection = e.target.checked
                                      ? [...chartColumns, col]
                                      : chartColumns.filter(c => c !== col);
                                    setChartColumns(newSelection);
                                    if (chartType === 'pie') setPieColumn(e.target.checked ? col : '');
                                  }}
                                />
                                <span className="mx-2">{col}</span>
                                {chartColumns.includes(col) && chartType !== 'pie' && (
                                  <input
                                    type="color"
                                    value={columnColors[col] || "#"+Math.floor(Math.random()*16777215).toString(16)}
                                    onChange={(e) => {
                                      setColumnColors(prev => ({ ...prev, [col]: e.target.value }));
                                    }}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="bg-white flex justify-center items-center min-h-screen">

                          <ChartSection
                            chartType={chartType}
                            chartColumns={chartColumns}
                            columnColors={columnColors}
                            parsedData= {merged}
                            labelChart={labelChart}
                            formTitle={form.name}
                          />
                        </div>

                        

                        {displayedResponses.length > 0 ? (
                          <div className="table-wrapper" style={{ overflowX: 'auto' }}>
                            <table className="table table-bordered text-white">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  {fields.map((field, i) => (
                                    <th key={i}>{field.label}</th>
                                  ))}
                                  <th>Location</th>
                                  <th>Submitted At</th>
                                  {dynamicColumns.map((col, idx) => (
                                    <th key={`dynamic-${idx}`}>
                                      <div className="d-flex align-items-center justify-content-between">
                                        <span>{col}</span>
                                        <button
                                          onClick={() => removeColumn(col)}
                                          className="btn btn-sm btn-danger ms-2"
                                          style={{ padding: '2px 6px', fontSize: '12px' }}
                                        >
                                          ✖️
                                        </button>
                                      </div>
                                    </th>
                                  ))}
                                </tr>
                              </thead>

                              <tbody>
                                {displayedResponses.map((entry, i) => (
                                  <tr key={entry.id || i}>
                                    <td>{i + 1}</td>
                                    {fields.map((field, j) => {
                                      const value = entry.parsed[field.name];
                                      const { type, content } = detectType(value);
                                      let displayValue = '';

                                      switch (type) {
                                        case 'image':
                                        case 'video':
                                        case 'audio':
                                        case 'document':
                                        case 'map':
                                          displayValue = (
                                            <button
                                              className="btn btn-link p-0 text-info"
                                              onClick={() => openModal(content, type)}
                                            >
                                              {type === 'map' ? 'View Map' : 'View ' + type.charAt(0).toUpperCase() + type.slice(1)}
                                            </button>
                                          );
                                          break;
                                        case 'link':
                                          displayValue = (
                                            <a href={content} className="btn btn-link p-0 text-info" target="_blank" rel="noopener noreferrer">
                                              Open Link
                                            </a>
                                          );
                                          break;
                                        default:
                                          try {
                                            let realContent = content;
                                            if (typeof content === 'string' && (content.startsWith('{') || content.startsWith('['))) {
                                              realContent = JSON.parse(content);
                                            }

                                            if (Array.isArray(realContent)) {
                                              displayValue = realContent.length ? realContent.join(', ') : 'NA';
                                            } else if (typeof realContent === 'object' && realContent !== null) {
                                              displayValue = Object.keys(realContent).length ? JSON.stringify(realContent) : 'NA';
                                            } else if (realContent === null || realContent === undefined || realContent === '') {
                                              displayValue = 'NA';
                                            } else {
                                              displayValue = typeof realContent === 'string' ? realContent.replace(/^"|"$/g, '') : realContent;
                                            }
                                          } catch (e) {
                                            displayValue = content || 'NA';
                                          }
                                      }

                                      return <td key={j}>{displayValue}</td>;
                                    })}
                                    <td>
                                      {(entry.lat && entry.long) ? (
                                        <button
                                          className="btn btn-link p-0 text-info"
                                          onClick={() => openModal({ lat: entry.lat, long: entry.long }, 'map')}
                                        >
                                          View Map
                                        </button>
                                      ) : <>NA</>}
                                    </td>
                                    <td>{new Date(entry.created_at).toLocaleString()}</td>
                                    {dynamicColumns.map((col, colIdx) => (
                                      <td key={`dyn-input-${colIdx}`}>
                                        <input
                                          type="text"
                                          value={classifications[entry.id]?.[col] || ''}
                                          onChange={(e) => handleClassificationChange(entry.id, col, e.target.value)}
                                          className="border rounded p-1"
                                        />
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p>No submissions yet.</p>
                        )}
                      </div>

                      </>: <p>No submissions yet.</p> }

                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* Modal */}
      {modalContent && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(160, 201, 162, 0.7)' }}>
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content bg-light text-primary">
              <div className="modal-header">
                <h5 className="modal-title">Preview</h5>
                <button type="button" className="btn-close btn-close-danger" onClick={closeModal}></button>
              </div>
              <div className="modal-body text-center">
                {modalType === 'image' && <img src={domain+modalContent} alt="Preview" className="img-fluid" />}
                {modalType === 'video' && (
                  <video controls className="w-100">
                    <source src={domain+modalContent} />
                    Your browser does not support the video tag.
                  </video>
                )}
                {modalType === 'audio' && (
                  <audio controls className="w-100">
                    <source src={domain+modalContent} />
                    Your browser does not support the audio element.
                  </audio>
                )}
                {modalType === 'document' && (
                  <iframe src={domain+modalContent} title="Document Preview" width="100%" height="500px"></iframe>
                )}
                {modalType === 'map' && (
                  <iframe
                    width="100%"
                    height="400"
                    frameBorder="0"
                    style={{ border: 0 }}
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://www.google.com/maps?q=${modalContent.lat},${modalContent.long}&z=15&output=embed`}
                    allowFullScreen
                  ></iframe>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer subpage={true}/>
    </>



  );

}

function generateFieldStats(responses, field) {
  const counts = {};
  responses.forEach(res => {
    const answer = res.responses[field];
    counts[answer] = (counts[answer] || 0) + 1;
  });
  return counts;
}


export default DataFormAnswer;
